import React, { useEffect, useRef } from 'react';

/**
 * Full-viewport "Matrix" digital rain on a fixed canvas behind all content.
 *
 * Tuned to be easy on weaker devices:
 *  - ~18fps throttle (not 60).
 *  - No canvas shadowBlur (the single most expensive 2D op) — the leading
 *    glyph is just drawn in a brighter colour.
 *  - Animation is skipped entirely (a single static frame is painted instead)
 *    when the device is likely to struggle or the user opted out: mobile-width
 *    screens, low CPU core counts, Save-Data, or prefers-reduced-motion.
 *  - The loop pauses while the tab/page is hidden.
 */
const GLYPHS =
  'アァカサタナハマヤャラワガザダバパイィキシチニヒミリヰギジヂビピウゥクスツヌフムユュルグズブヅプ0123456789ABCDEFZ$+*<>=/\\';

const MatrixRain: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const nav = navigator as Navigator & { deviceMemory?: number; connection?: { saveData?: boolean } };
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = !!nav.connection?.saveData;
    const lowCores = typeof nav.hardwareConcurrency === 'number' && nav.hardwareConcurrency <= 2;
    const lowMemory = typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 2;
    const smallScreen = window.innerWidth < 768;
    // On phones, low-end hardware, or when the user/network asks us to go easy,
    // render a static field once and never start the animation loop.
    const animate = !(reduceMotion || saveData || lowCores || lowMemory || smallScreen);

    const fontSize = 16;
    let columns = 0;
    let drops: number[] = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      columns = Math.floor(canvas.width / fontSize);
      drops = new Array(columns).fill(0).map(() => Math.random() * -50);
    };
    resize();

    const draw = () => {
      // Translucent black fade leaves trailing streaks.
      ctx.fillStyle = 'rgba(0, 4, 0, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = `${fontSize}px 'Share Tech Mono', monospace`;

      for (let i = 0; i < drops.length; i++) {
        const char = GLYPHS.charAt(Math.floor(Math.random() * GLYPHS.length));
        const x = i * fontSize;
        const y = drops[i] * fontSize;
        // Leading glyph brighter; trail dimmer green. No shadowBlur (too costly).
        ctx.fillStyle = Math.random() > 0.975 ? '#d8ffe0' : 'rgba(0, 255, 65, 0.55)';
        ctx.fillText(char, x, y);

        if (y > canvas.height && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      }
    };

    // Paint a solid backdrop first so the first frames aren't transparent.
    ctx.fillStyle = '#000400';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (!animate) {
      // Build a fuller static image, then stop — no ongoing CPU/GPU cost.
      for (let k = 0; k < 40; k++) draw();
      return;
    }

    window.addEventListener('resize', resize);

    let raf = 0;
    let last = 0;
    const FRAME_MS = 55; // ~18fps, plenty for rain and easy on the CPU
    const loop = (t: number) => {
      // Skip work while the page is hidden (also a battery saver).
      if (!document.hidden && t - last >= FRAME_MS) {
        draw();
        last = t;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        background: '#000400',
        pointerEvents: 'none',
      }}
    />
  );
};

export default MatrixRain;
